/* storaskuggan-details.js
   Smooth "opens downward" Details accordion for the product page.

   Why JS:
   - CSS-only solutions often stutter when content includes images, rich text reflow,
     or when the browser has to recalc layout mid-transition.
   - This script animates an explicit height from 0 -> content height (and back),
     while keeping opacity/transform in sync (no flicker / "light glitches").

   Scope:
   - Only runs on <details class="stsk-details"> inside <product-info class="stsk-pdp">.
*/
(() => {
  const DETAILS_SELECTOR = "product-info.stsk-pdp details.stsk-details";
  const SUMMARY_SELECTOR = "summary";
  const PANEL_SELECTOR   = ".stsk-details__content";
  const INNER_SELECTOR   = ".stsk-details__inner";

  // Timing (tweak if you want snappier / slower)
  const OPEN_MS  = 420;
  const CLOSE_MS = 260;

  const prefersReducedMotion = () =>
    window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const easeOutQuint = (t) => 1 - Math.pow(1 - t, 5);
  const easeInCubic  = (t) => t * t * t;
  const px = (n) => `${Math.max(0, n)}px`;

  const ensureInner = (panel) => {
    let inner = panel.querySelector(INNER_SELECTOR);
    if (!inner) {
      inner = document.createElement("div");
      inner.className = INNER_SELECTOR.replace(".", "");
      while (panel.firstChild) inner.appendChild(panel.firstChild);
      panel.appendChild(inner);
    }
    return inner;
  };

  const animateHeight = (panel, from, to, ms, easing, tokenCheck, onDone) => {
    cancelAnimationFrame(panel.__stskRAF);

    const start = performance.now();
    const delta = to - from;

    const tick = (now) => {
      if (!tokenCheck()) return;

      const t = Math.min(1, (now - start) / ms);
      panel.style.height = px(from + delta * easing(t));

      if (t < 1) panel.__stskRAF = requestAnimationFrame(tick);
      else {
        panel.__stskRAF = null;
        onDone && onDone();
      }
    };
    panel.__stskRAF = requestAnimationFrame(tick);
  };

  const initOne = (details) => {
    if (details.__stskInit) return;
    details.__stskInit = true;

    const summary = details.querySelector(SUMMARY_SELECTOR);
    const panel   = details.querySelector(PANEL_SELECTOR);
    if (!summary || !panel) return;

    const inner = ensureInner(panel);

    // JS owns height (prevents snap + keeps animation stable)
    panel.style.display = "block";
    panel.style.overflow = "hidden";
    panel.style.willChange = "height";

    let state = details.hasAttribute("open") ? "open" : "closed"; // opening/closing
    let opId = 0;

    const applyVisualOpen = (open) => {
      details.classList.toggle("is-expanded", open);
      // Opacity/transform are handled by CSS, but we set sane fallbacks:
      panel.style.opacity = open ? "1" : "0.12";
      panel.style.transform = open ? "translateY(0)" : "translateY(-3px)";
    };

    const setNativeOpen = (open) => {
      if (open) details.setAttribute("open", "");
      else details.removeAttribute("open");
    };

    // Initial render
    if (state === "open") {
      applyVisualOpen(true);
      panel.style.height = px(inner.scrollHeight);
    } else {
      applyVisualOpen(false);
      panel.style.height = "0px";
      setNativeOpen(false);
    }

    const open = () => {
      opId++;
      const my = opId;

      if (prefersReducedMotion()) {
        state = "open";
        setNativeOpen(true);
        applyVisualOpen(true);
        panel.style.height = px(inner.scrollHeight);
        return;
      }

      setNativeOpen(true);
      const from = panel.getBoundingClientRect().height;

      requestAnimationFrame(() => {
        if (my !== opId) return;

        const to = inner.scrollHeight;

        state = "opening";
        applyVisualOpen(true);

        animateHeight(panel, from, to, OPEN_MS, easeOutQuint, () => my === opId, () => {
          if (my !== opId) return;
          state = "open";
          panel.style.height = px(inner.scrollHeight); // keep numeric height (no snap)
        });
      });
    };

    const close = () => {
      opId++;
      const my = opId;

      if (prefersReducedMotion()) {
        state = "closed";
        applyVisualOpen(false);
        setNativeOpen(false);
        panel.style.height = "0px";
        return;
      }

      const from = panel.getBoundingClientRect().height;
      const to = 0;

      state = "closing";
      applyVisualOpen(false);

      animateHeight(panel, from, to, CLOSE_MS, easeInCubic, () => my === opId, () => {
        if (my !== opId) return;
        state = "closed";
        setNativeOpen(false);
        panel.style.height = "0px";
      });
    };

    const toggle = () => {
      if (state === "open" || state === "opening") close();
      else open();
    };

    summary.addEventListener("click", (e) => {
      e.preventDefault();
      toggle();
    });

    // Keep keyboard behavior accessible when we prevent default click
    summary.addEventListener("keydown", (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        toggle();
      }
    });

    // Keep height correct if content reflows while open (images loading, font swaps, etc.)
    const ro = new ResizeObserver(() => {
      if (!(state === "open" || state === "opening")) return;

      opId++;
      const my = opId;

      const from = panel.getBoundingClientRect().height;
      const to = inner.scrollHeight;

      animateHeight(panel, from, to, 180, easeOutQuint, () => my === opId, () => {
        if (my !== opId) return;
        state = "open";
        panel.style.height = px(inner.scrollHeight);
      });
    });
    ro.observe(inner);
  };

  const initAll = (root = document) => {
    root.querySelectorAll(DETAILS_SELECTOR).forEach(initOne);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initAll());
  } else {
    initAll();
  }

  // Theme editor support
  document.addEventListener("shopify:section:load", (e) => initAll(e.target));
})();
